import React from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import _ from 'lodash';
import { Cheque } from '@baemingo/table-events/dist/types';

import { Content, Wrapper, Header, ContentInner, Footer } from '~/Components/LayoutLeftSidebar';
import PrintButton from '~/Components/PrintButton';
import SelectedOrder from './Components/SelectedOrder';
import { HistoryOrder } from '.';
import {
  useReceiptGenerator,
  ReceiptType,
  useI18n,
  useStaffCan,
  useSelector,
  useDispatch,
} from '~Hooks';
import SelectedCheque from './Components/SelectedCheque';
import OrderList from './Components/OrderList';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import { useReportsApi } from '~Components/ReportsUniverse';
import { CardDetails } from '~reducers';
import Button from '~ComponentsImproved/Button';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import Refunds from '~Scenes/Refunds';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { PrintRow } from '~Components/PrinterManager';
import BongMaster from '~Scenes/Tables/Components/BongMaster';
import { ROLE } from '../../../__queries__';
import { BlueButton } from '~Styles';
import { NavLink } from 'react-router-dom';

const LeftBlock = styled.div`
  display: flex;
  flex-grow: 1;
`;

const RightBlock = styled.div`
  display: flex;
  margin-left: auto;
`;

const Arrow = styled.i.attrs({
  className: 'icon arrow_left',
})`
  font-size: 26px;
  font-weight: bold !important;
`;

const ArrowWrapper = styled.div<{ disabled: boolean }>`
  padding: 16px;
  color: ${({ disabled }) => (disabled ? '#999' : '#000')};
`;

const ArrowTop = styled(ArrowWrapper)`
  transform: rotate(90deg);
`;

const ArrowBottom = styled(ArrowWrapper)`
  transform: rotate(-90deg);
`;

const InfoBanner = styled.div`
  background-color: var(--colorUISand);
  border: solid 2px var(--colorUIYellow);
  height: max-content;
  max-height: 80px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 8px 8px 8px 16px;
  box-sizing: border-box;
  border-radius: 4px;
`;

const FooterWrapper = styled(Footer)`
  border-top: solid 2px #d2d2d2;
`;

const InfoBar = styled.div`
  padding: 16px 20px;
  font-size: 14px;
  background: rgba(22, 67, 190, 0.8);
  color: white;
  border-bottom: #1642be;
`;

const CHANGE_HAS_PRINTED = gql`
  mutation updateOrder($id: ID!, $hasPrintedReceiptCopy: Boolean) {
    updateOrder(id: $id, hasPrintedReceiptCopy: $hasPrintedReceiptCopy) {
      id
      hasPrintedReceiptCopy
    }
  }
`;

interface Props {
  venueId: string;
  companyId: string;
  selectedOrderId: string | null;
  setSelectedOrderId: (id: string | null) => void;
  selectedOrder?: HistoryOrder | null;
  setSelectedCheque: (cheque: Cheque | undefined) => void;
  selectedCheque: Cheque | undefined;
}

export interface PrintReceiptArgs {
  type: ReceiptType;
  order?: HistoryOrder | null;
  itemId?: string;
  cardDetails?: CardDetails;
  cardIdentifier?: string;
  cardMethod?: string;
  isCopy?: boolean;
}

const HistoryView = ({
  venueId,
  companyId,
  selectedOrderId,
  setSelectedOrderId,
  selectedOrder,
  setSelectedCheque,
  selectedCheque,
}: Props): JSX.Element => {
  const staff = useSelector((state) => state.staff);
  const flags = useSelector((state) => state.companyFlags);
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const country = useSelector((state) => state.country);
  const reportsApi = useReportsApi();
  const [changeHasPrinted] = useMutation(CHANGE_HAS_PRINTED);
  const { i18n } = useI18n();
  const generateReceipt = useReceiptGenerator({ venueId });
  const staffCan = useStaffCan();
  const dispatch = useDispatch();
  const { isShowing, toggle } = useModal();

  const printReceipt = async ({
    type,
    order = selectedOrder,
    itemId,
    cardDetails,
    cardIdentifier,
    cardMethod,
    isCopy,
  }: PrintReceiptArgs): Promise<void> => {
    if (!order) return;

    const [
      _complianceId = undefined,
      _receiptCounter = undefined,
      deviceRegNumber,
      deviceId,
    ] = order.basketToken.split('-');

    let items = [];
    let total: number;

    if (!itemId) {
      // order
      items = order.items
        .filter((item) => !item.isRefunded) // on refund
        .map(({ entry, price, fraction, variations, removedVariations }) => ({
          entryId: entry.id,
          amount: price.amount,
          discount: price.discount,
          percentageVAT: price.percentageVAT,
          variations: variations?.map((variation) => i18n(variation.nameLang)),
          removedVariations: removedVariations?.map((variation) => i18n(variation.nameLang)),
          title: i18n(entry.item.titleLang),
          fraction,
        }));

      const amount = _.sumBy(items, (item) => item.amount - item.discount);
      total = type === ReceiptType.Refund ? -amount : amount;
    } else {
      // order item
      const item = order.items.find((i) => i.id === itemId);
      const { entry, price, fraction, variations, removedVariations } = item!;
      items = [
        {
          entryId: entry.id,
          amount: price.amount,
          discount: price.discount,
          percentageVAT: price.percentageVAT,
          variations: variations.map((variation) => i18n(variation.nameLang)),
          removedVariations: removedVariations?.map((variation) => i18n(variation.nameLang)),
          title: i18n(entry.item.titleLang),
          fraction,
        },
      ];

      // only refund case
      total = -(price.amount - price.discount);
    }

    const totalDiscount = _.sumBy(items, (item) => item.discount);

    const tip = order.payment.correctedTip ?? order.payment.tip;

    let userId: string | undefined = undefined;
    const isBackofficeAdmin =
      order?.user.role === ROLE.CONTENT_ADMIN || (order?.user.venues ?? []).length > 0;

    if (!isBackofficeAdmin && country === 'ES') {
      userId = order?.user?.id;
    }

    const receipt = await generateReceipt({
      items,
      totalDiscount,
      type,
      isTable: false,
      isCopy,
      cardDetails,
      payments: [
        {
          type: order.payment.type,
          amount: (total / 100).toFixed(2),
        },
      ],
      extraVars: {
        tip: itemId ? undefined : tip.toString(),
        paymentIncTip: itemId ? undefined : (total + tip).toFixed(2),
        checkId: (receiptCounter + 1).toString() ?? '0',
        itemsTotal: (total / 100).toFixed(2),
        deviceName: order.posDevice?.name,
        deviceId,
        deviceRegNumber,
        datetime: order.createdAt,
        cardMethod: cardMethod,
        cardIdentifier: cardIdentifier,
      },
      userId,
    });

    if (receipt) {
      const message: PrintRow[] = receipt.lines.map((line) => ({
        fontSize: line.fontSize || (line.doubleHeigth || line.doubleWidth ? 26 : 13), // doubleHeight here can come from the receipt package
        content: line.text,
      }));

      const QRContent = flags.hasPoints
        ? `claim:${order.payment.transactionId}:${total}:org_8AGDOueNZxuxTJ0PfaZk5`
        : undefined;

      if (QRContent) {
        message.push({
          content: QRContent,
          qr: true,
        });
      }

      window.printerManager?.initiatePrint({
        data: message,
        printType: 'RECEIPT',
        base64Image: receipt.base64Image,
      });

      // Increase receipt counter
      dispatch({
        type: 'INCREASE_RECEIPT_COUNTER',
      });

      const orderId = order.id;

      reportsApi.receiptCopyPrinted(total);

      changeHasPrinted({
        variables: { id: orderId, hasPrintedReceiptCopy: true },
      });
      order.hasPrintedReceiptCopy = true;
    }
  };

  //If there are items that has not been refunded or if there is a tip that has not been refunded, then refund is possible
  const refundPossible =
    selectedOrder?.status === 'COMPLETED' &&
    (selectedOrder?.items.some((item) => !item.isRefunded) ||
      (selectedOrder?.tip && selectedOrder?.tip > 0));

  const orderTitle = selectedOrder?.easyId
    ? selectedOrder.easyId.includes('•')
      ? selectedOrder.easyId
      : '#' + selectedOrder.easyId
    : selectedOrder?.id;

  return (
    <Wrapper>
      {companyId && (
        <OrderList
          companyId={companyId}
          venueId={venueId}
          selectedCheque={selectedCheque}
          setSelectedCheque={setSelectedCheque}
          selectedOrderId={selectedOrderId}
          setSelectedOrderId={setSelectedOrderId}
        />
      )}
      <Content>
        <Header
          title={
            selectedCheque
              ? `${i18n('History.Receipt')} #${selectedCheque.id.substring(0, 4).toUpperCase()}`
              : selectedOrder
              ? `${i18n('History.Receipt')} ${orderTitle}`
              : null
          }
          textAlign={'center'}
        />
        {!staffCan.REFUND && (selectedOrderId || selectedCheque) && (
          <InfoBar>{i18n('History.RefundMissingPermission')}</InfoBar>
        )}
        <ContentInner>
          {!selectedOrder && (
            <InfoBanner>
              {i18n('History.Legacy.Info')}{' '}
              <NavLink to={`/venue/${venueId}/history-v2`}>
                <BlueButton>{i18n('History.Legacy.Info.Button')}</BlueButton>
              </NavLink>
            </InfoBanner>
          )}
          {selectedOrder?.id === selectedOrderId && (
            <SelectedOrder selectedOrder={selectedOrder} printReceipt={printReceipt} />
          )}
          {selectedCheque && <SelectedCheque selectedCheque={selectedCheque}></SelectedCheque>}
        </ContentInner>
        <FooterWrapper>
          <LeftBlock>
            {selectedOrder && <PrintButton order={selectedOrder} printReceipt={printReceipt} />}
          </LeftBlock>
          {staffCan.REFUND && selectedOrderId && (
            <div>
              <Button
                styleType="OK"
                text={
                  <div>
                    {i18n('History.Refund')}
                    <Paragraph level={5} color="#fff">
                      {i18n('History.Refunds.OpenNewWindow')}
                    </Paragraph>
                  </div>
                }
                width="156px"
                height="46px"
                disabled={!refundPossible}
                onTouchStart={toggle}
                disabledToast={
                  selectedOrder?.status === 'COMPLETED' || selectedOrder?.status === 'REFUNDED'
                    ? i18n('Refunds.Disabled.AlreadyRefunded')
                    : i18n('Refunds.Disabled.OrderNotCompleted')
                }
              />
            </div>
          )}
        </FooterWrapper>
      </Content>
      {staff === null && <LoginModal />}

      {isShowing && selectedOrderId && selectedOrder && (
        <Modal isShowing={isShowing} noPadding hide={toggle}>
          <Refunds orderId={selectedOrderId} selectedOrder={selectedOrder} closeModal={toggle} />
        </Modal>
      )}
      <BongMaster />
    </Wrapper>
  );
};

export default HistoryView;
